import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Alert,
} from '@mui/material';
import { User } from '../../services/userService';

interface ChangeEmailDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (newEmail: string) => void;
  user?: User;
  loading?: boolean;
}

const ChangeEmailDialog: React.FC<ChangeEmailDialogProps> = ({
  open,
  onClose,
  onSubmit,
  user,
  loading = false,
}) => {
  const [newEmail, setNewEmail] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (user && open) {
      setNewEmail(user.email);
    } else {
      setNewEmail('');
    }
    setError('');
  }, [user, open]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newEmail.trim()) {
      setError('Email is required');
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail)) {
      setError('Please enter a valid email address');
      return;
    }

    if (newEmail === user?.email) {
      setError('New email must be different from current email');
      return;
    }

    onSubmit(newEmail);
  };

  const handleClose = () => {
    setError('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          Change Email Address
        </DialogTitle>
        
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            {user && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Changing email for: <strong>{user.firstName} {user.lastName}</strong>
              </Alert>
            )}
            
            <TextField
              fullWidth
              label="Current Email"
              value={user?.email || ''}
              disabled
              sx={{ mb: 2 }}
              helperText="Current email address"
            />
            
            <TextField
              fullWidth
              label="New Email Address"
              type="email"
              value={newEmail}
              onChange={(e) => {
                setNewEmail(e.target.value);
                if (error) setError('');
              }}
              error={!!error}
              helperText={error || 'Enter the new email address'}
              disabled={loading}
              autoFocus
            />

            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              <strong>Note:</strong> The user will need to use the new email address for future logins.
            </Typography>
          </Box>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading || !newEmail.trim() || newEmail === user?.email}
          >
            {loading ? 'Changing...' : 'Change Email'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ChangeEmailDialog;
